// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Patient } from '@medplum/fhirtypes';

export type IntakeFormState = 'complete' | 'awaiting-np' | 'not-applicable';

// Tags written by process-eligibility-step's state machine. 'finished' lands
// after the bot creates the intake Composition + Conditions + Observations.
// 'appointment-booked' is the entry tag set when the patient picks the
// scheduled-appointment branch on the eligibility-confirmation page.
const COMPLETED_TAG = 'eligibility-check-finished';

// Any of these means the NP can still click "Complete" and resume from the
// patient's current step. 'appointment-booked' is the pre-first-step entry;
// the others are intermediate phases set by the bot after each successful
// submit. Without all four intermediates here, closing the form mid-flow
// flips the encounter-note card to "Not captured yet" and hides the resume
// CTA even though the page-level resume logic in NpIntakeFormPage still
// works if the NP navigates to the URL directly.
const AWAITING_NP_TAGS: readonly string[] = [
  'eligibility-appointment-booked',
  'eligibility-check-page-1',
  'eligibility-check-page-2',
  'stop-bang',
  'epworth-sleepiness-scale',
];

/**
 * Classifies a patient's intake-form state for the encounter-note Supplemental
 * Information card. Pure today; kept as a hook so Phase 3 can swap in cache
 * invalidation without changing call sites.
 */
export function useIntakeFormState(patient: Patient | undefined): IntakeFormState {
  if (!patient) {
    return 'not-applicable';
  }
  const codes = patient.meta?.tag?.map((t) => t.code) ?? [];
  if (codes.includes(COMPLETED_TAG)) {
    return 'complete';
  }
  if (codes.some((code) => code !== undefined && AWAITING_NP_TAGS.includes(code))) {
    return 'awaiting-np';
  }
  return 'not-applicable';
}
